import React, { useState, useMemo } from 'react';
import { Exam, Category } from '../../types';
import { deleteExam } from '../../services/dataService';

interface AdminPapersProps {
    categories: Category[];
    exams: Exam[];
    onAddExam: (exam: Exam) => void;
    onUpdateExam: (exam: Exam) => void;
    onDeleteExam: (examId: string) => void;
}

const AdminPapers: React.FC<AdminPapersProps> = ({ categories, exams, onAddExam, onUpdateExam, onDeleteExam }) => {
    const [newPaper, setNewPaper] = useState<{
        id: string; title: string; category: string; subcategory: string;
        durationMinutes: number; totalQuestions: number;
        positiveMarks: number; negativeMarks: number; isPremium: boolean;
        startTime: string; endTime: string;
    }>({
        id: '',
        title: '',
        category: categories[0]?.id || '',
        subcategory: '',
        durationMinutes: 60,
        totalQuestions: 10,
        positiveMarks: 2,
        negativeMarks: 0.66,
        isPremium: false,
        startTime: '',
        endTime: ''
    });

    const [editingPaper, setEditingPaper] = useState<Exam | null>(null);

    // Filter and Sort state
    const [filterCategory, setFilterCategory] = useState<string>('all');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

    // Filtered and sorted exams
    const filteredExams = useMemo(() => {
        let result = [...exams];

        // Filter by category
        if (filterCategory !== 'all') {
            result = result.filter(e => e.category === filterCategory);
        }

        // Sort by ID (which usually contains date info) or by title alphabetically
        // Since we don't have createdAt, we'll sort by ID (newest IDs typically have higher values or recent dates)
        result.sort((a, b) => {
            const comparison = a.id.localeCompare(b.id);
            return sortOrder === 'asc' ? comparison : -comparison;
        });

        return result;
    }, [exams, filterCategory, sortOrder]);

    const submitPaper = (e: React.FormEvent) => {
        e.preventDefault();
        if (newPaper.id && newPaper.title) {
            const paper: Exam = {
                ...newPaper,
                questions: [],
                startTime: newPaper.startTime || undefined,
                endTime: newPaper.endTime || undefined
            };
            onAddExam(paper);
            setNewPaper({
                id: '',
                title: '',
                category: categories[0]?.id || '',
                subcategory: '',
                durationMinutes: 60,
                totalQuestions: 10,
                positiveMarks: 2,
                negativeMarks: 0.66,
                isPremium: false,
                startTime: '',
                endTime: ''
            });
            alert('Paper Added Successfully');
        }
    };

    const handleEditPaper = (exam: Exam) => {
        setEditingPaper(exam);
    };

    const handleSaveEdit = (e: React.FormEvent) => {
        e.preventDefault();
        if (editingPaper) {
            onUpdateExam(editingPaper);
            setEditingPaper(null);
            alert('Paper updated successfully!');
        }
    };

    const handleDeletePaper = async (examId: string) => {
        if (window.confirm('Are you sure you want to delete this paper? This will also delete all associated questions.')) {
            await deleteExam(examId);
            onDeleteExam(examId);
            alert('Paper deleted successfully!');
        }
    };

    return (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-1">
                <div className="bg-white dark:bg-slate-800 p-6 rounded-lg shadow-sm border border-slate-200 dark:border-slate-700 sticky top-6">
                    <h3 className="text-lg font-bold text-slate-800 dark:text-white mb-4">Add Test Paper</h3>
                    <form onSubmit={submitPaper} className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Paper ID</label>
                            <input
                                type="text"
                                value={newPaper.id}
                                onChange={e => setNewPaper({ ...newPaper, id: e.target.value })}
                                placeholder="e.g. upsc-2025-mock-2"
                                className="w-full p-2 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:ring-2 focus:ring-orange-500 outline-none"
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Title</label>
                            <input
                                type="text"
                                value={newPaper.title}
                                onChange={e => setNewPaper({ ...newPaper, title: e.target.value })}
                                placeholder="e.g. UPSC GS Mock 2"
                                className="w-full p-2 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:ring-2 focus:ring-orange-500 outline-none"
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Category</label>
                            <select
                                value={newPaper.category}
                                onChange={e => setNewPaper({ ...newPaper, category: e.target.value, subcategory: '' })}
                                className="w-full p-2 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-orange-500 outline-none"
                            >
                                {categories.map(cat => (
                                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                                ))}
                            </select>
                        </div>

                        {/* Conditional Subcategory Dropdown */}
                        {categories.find(c => c.id === newPaper.category)?.subcategories &&
                            (categories.find(c => c.id === newPaper.category)?.subcategories?.length || 0) > 0 && (
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Subcategory</label>
                                    <select
                                        value={newPaper.subcategory}
                                        onChange={e => setNewPaper({ ...newPaper, subcategory: e.target.value })}
                                        className="w-full p-2 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-orange-500 outline-none"
                                    >
                                        <option value="">-- Select Subcategory --</option>
                                        {categories.find(c => c.id === newPaper.category)?.subcategories?.map(sub => (
                                            <option key={sub.id} value={sub.id}>{sub.name}</option>
                                        ))}
                                    </select>
                                </div>
                            )}

                        {/* Scheduling Section */}
                        <div className="p-4 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-100 dark:border-blue-900/30">
                            <h4 className="text-sm font-bold text-blue-800 dark:text-blue-300 mb-3">
                                <i className="far fa-calendar-alt mr-2"></i>Schedule (Optional - For Live Mocks)
                            </h4>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-medium text-blue-700 dark:text-blue-400 mb-1">Start Time</label>
                                    <input
                                        type="datetime-local"
                                        value={newPaper.startTime}
                                        onChange={e => setNewPaper({ ...newPaper, startTime: e.target.value })}
                                        className="w-full p-2 bg-white dark:bg-slate-900 border border-blue-200 dark:border-blue-880 rounded text-sm text-slate-900 dark:text-slate-100"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-blue-700 dark:text-blue-400 mb-1">End Time</label>
                                    <input
                                        type="datetime-local"
                                        value={newPaper.endTime}
                                        onChange={e => setNewPaper({ ...newPaper, endTime: e.target.value })}
                                        className="w-full p-2 bg-white dark:bg-slate-900 border border-blue-200 dark:border-blue-880 rounded text-sm text-slate-900 dark:text-slate-100"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Duration (min)</label>
                                <input
                                    type="number"
                                    value={newPaper.durationMinutes}
                                    onChange={e => setNewPaper({ ...newPaper, durationMinutes: parseInt(e.target.value) })}
                                    className="w-full p-2 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-orange-500 outline-none"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Questions</label>
                                <input
                                    type="number"
                                    value={newPaper.totalQuestions}
                                    onChange={e => setNewPaper({ ...newPaper, totalQuestions: parseInt(e.target.value) })}
                                    className="w-full p-2 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-orange-500 outline-none"
                                />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">+ Marks</label>
                                <input
                                    type="number"
                                    step="0.1"
                                    value={newPaper.positiveMarks}
                                    onChange={e => setNewPaper({ ...newPaper, positiveMarks: parseFloat(e.target.value) })}
                                    className="w-full p-2 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-orange-500 outline-none"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">- Marks</label>
                                <input
                                    type="number"
                                    step="0.1"
                                    value={newPaper.negativeMarks}
                                    onChange={e => setNewPaper({ ...newPaper, negativeMarks: parseFloat(e.target.value) })}
                                    className="w-full p-2 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-orange-500 outline-none"
                                />
                            </div>
                        </div>

                        <div className="flex items-center">
                            <input
                                type="checkbox"
                                id="isPremium"
                                checked={newPaper.isPremium}
                                onChange={e => setNewPaper({ ...newPaper, isPremium: e.target.checked })}
                                className="w-4 h-4 text-orange-600 border-slate-300 dark:border-slate-700 rounded focus:ring-orange-500"
                            />
                            <label htmlFor="isPremium" className="ml-2 block text-sm font-medium text-slate-700 dark:text-slate-300">
                                Premium Content <span className="text-xs text-slate-400 dark:text-slate-500">(Paid users only)</span>
                            </label>
                        </div>

                        <button type="submit" className="w-full bg-slate-900 dark:bg-slate-700 hover:bg-slate-800 dark:hover:bg-slate-600 text-white py-2 rounded font-medium transition-colors">
                            Create Paper
                        </button>
                    </form>
                </div>
            </div>

            <div className="lg:col-span-2">
                <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden">
                    <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 flex flex-wrap justify-between items-center gap-4">
                        <h3 className="font-bold text-slate-800 dark:text-white">Existing Papers</h3>
                        <div className="flex items-center gap-3">
                            {/* Category Filter */}
                            <div className="flex items-center gap-2">
                                <label className="text-sm text-slate-600 dark:text-slate-400">Category:</label>
                                <select
                                    value={filterCategory}
                                    onChange={e => setFilterCategory(e.target.value)}
                                    className="text-sm bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded text-slate-750 dark:text-slate-300 px-2 py-1 focus:ring-2 focus:ring-orange-500 outline-none"
                                >
                                    <option value="all">All Categories</option>
                                    {categories.map(cat => (
                                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Sort Toggle */}
                            <button
                                onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                                className="flex items-center gap-1 px-3 py-1.5 text-sm bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                                title={`Sort ${sortOrder === 'asc' ? 'Descending' : 'Ascending'}`}
                            >
                                <span className="text-slate-600 dark:text-slate-400">Sort</span>
                                <i className={`fas fa-sort-${sortOrder === 'asc' ? 'up' : 'down'} text-orange-500`}></i>
                            </button>

                            <span className="text-sm bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 px-2 py-1 rounded text-slate-500 dark:text-slate-400">
                                {filteredExams.length} / {exams.length}
                            </span>
                        </div>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-left">
                            <thead>
                                <tr className="bg-slate-50 dark:bg-slate-800/30 text-slate-600 dark:text-slate-400 text-sm">
                                    <th className="px-6 py-3 font-semibold">ID</th>
                                    <th className="px-6 py-3 font-semibold">Title</th>
                                    <th className="px-6 py-3 font-semibold">Category</th>
                                    <th className="px-6 py-3 font-semibold">Schedule</th>
                                    <th className="px-6 py-3 font-semibold">Type</th>
                                    <th className="px-6 py-3 font-semibold text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                                {filteredExams.map((exam) => (
                                    <tr key={exam.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/30">
                                        <td className="px-6 py-4 text-sm font-mono text-slate-600 dark:text-slate-400 max-w-[120px] truncate" title={exam.id}>{exam.id}</td>
                                        <td className="px-6 py-4 font-medium text-slate-800 dark:text-slate-200">
                                            {exam.title}
                                        </td>
                                        <td className="px-6 py-4 text-sm">
                                            <span className="px-2 py-1 bg-slate-100 dark:bg-slate-900 text-slate-700 dark:text-slate-300 rounded text-xs font-medium">
                                                {categories.find(c => c.id === exam.category)?.name || exam.category}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-sm">
                                            {exam.startTime ? (
                                                <div className="text-xs">
                                                    <div className="text-green-600 font-medium dark:text-green-400">Start: {new Date(exam.startTime).toLocaleString()}</div>
                                                    <div className="text-red-500 dark:text-red-400">End: {exam.endTime ? new Date(exam.endTime).toLocaleString() : 'N/A'}</div>
                                                </div>
                                            ) : (
                                                <span className="text-slate-400 dark:text-slate-500 text-xs italic">On Demand</span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 text-sm">
                                            {exam.isPremium ? (
                                                <span className="px-2 py-1 bg-purple-100 dark:bg-purple-950/30 text-purple-700 dark:text-purple-400 rounded text-xs font-bold">Premium</span>
                                            ) : (
                                                <span className="px-2 py-1 bg-green-100 dark:bg-green-950/30 text-green-700 dark:text-green-400 rounded text-xs font-bold">Free</span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <button
                                                onClick={() => handleEditPaper(exam)}
                                                className="text-slate-400 dark:text-slate-500 hover:text-blue-600 dark:hover:text-blue-400 mx-1 p-2 hover:bg-blue-50 dark:hover:bg-blue-950/20 rounded transition-colors shadow-sm"
                                                title="Edit"
                                            >
                                                <i className="fas fa-edit"></i>
                                            </button>
                                            <button
                                                onClick={() => handleDeletePaper(exam.id)}
                                                className="text-slate-400 dark:text-slate-500 hover:text-red-600 dark:hover:text-red-400 mx-1 p-2 hover:bg-red-50 dark:hover:bg-red-950/20 rounded transition-colors shadow-sm"
                                                title="Delete"
                                            >
                                                <i className="fas fa-trash"></i>
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* Edit Modal */}
            {editingPaper && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/60 backdrop-blur-sm">
                    <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-lg w-full max-w-xl max-h-[90vh] overflow-hidden flex flex-col">
                        <div className="p-4 border-b border-slate-100 dark:border-slate-700 flex justify-between items-center bg-slate-50 dark:bg-slate-800/50">
                            <h3 className="font-bold text-slate-800 dark:text-white">Edit Paper</h3>
                            <button onClick={() => setEditingPaper(null)} className="text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-400">
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                        <div className="p-6 overflow-y-auto flex-1">
                            <form id="edit-paper-form" onSubmit={handleSaveEdit} className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Paper ID</label>
                                    <input
                                        type="text"
                                        value={editingPaper.id}
                                        disabled
                                        className="w-full p-2 border border-slate-200 dark:border-slate-700 rounded bg-slate-100 dark:bg-slate-900 text-slate-500 dark:text-slate-450"
                                    />
                                    <p className="text-xs text-slate-400 dark:text-slate-550 mt-1">ID cannot be changed</p>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Title</label>
                                    <input
                                        type="text"
                                        value={editingPaper.title}
                                        onChange={e => setEditingPaper({ ...editingPaper, title: e.target.value })}
                                        className="w-full p-2 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-orange-500 outline-none"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Category</label>
                                    <select
                                        value={editingPaper.category}
                                        onChange={e => setEditingPaper({ ...editingPaper, category: e.target.value })}
                                        className="w-full p-2 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-orange-500 outline-none"
                                    >
                                        {categories.map(cat => (
                                            <option key={cat.id} value={cat.id}>{cat.name}</option>
                                        ))}
                                    </select>
                                </div>

                                {/* Scheduling Section for Edit */}
                                <div className="p-4 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-100 dark:border-blue-900/30">
                                    <h4 className="text-sm font-bold text-blue-800 dark:text-blue-300 mb-3">
                                        <i className="far fa-calendar-alt mr-2"></i>Schedule (For Live Mocks)
                                    </h4>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-xs font-medium text-blue-700 dark:text-blue-400 mb-1">Start Time</label>
                                            <input
                                                type="datetime-local"
                                                value={editingPaper.startTime || ''}
                                                onChange={e => setEditingPaper({ ...editingPaper, startTime: e.target.value })}
                                                className="w-full p-2 bg-white dark:bg-slate-900 border border-blue-200 dark:border-blue-800 rounded text-sm text-slate-900 dark:text-slate-100"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium text-blue-700 dark:text-blue-400 mb-1">End Time</label>
                                            <input
                                                type="datetime-local"
                                                value={editingPaper.endTime || ''}
                                                onChange={e => setEditingPaper({ ...editingPaper, endTime: e.target.value })}
                                                className="w-full p-2 bg-white dark:bg-slate-900 border border-blue-200 dark:border-blue-800 rounded text-sm text-slate-900 dark:text-slate-100"
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Duration (min)</label>
                                        <input
                                            type="number"
                                            value={editingPaper.durationMinutes}
                                            onChange={e => setEditingPaper({ ...editingPaper, durationMinutes: parseInt(e.target.value) })}
                                            className="w-full p-2 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-orange-500 outline-none"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Questions</label>
                                        <input
                                            type="number"
                                            value={editingPaper.totalQuestions}
                                            onChange={e => setEditingPaper({ ...editingPaper, totalQuestions: parseInt(e.target.value) })}
                                            className="w-full p-2 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-orange-500 outline-none"
                                        />
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">+ Marks</label>
                                        <input
                                            type="number"
                                            step="0.1"
                                            value={editingPaper.positiveMarks}
                                            onChange={e => setEditingPaper({ ...editingPaper, positiveMarks: parseFloat(e.target.value) })}
                                            className="w-full p-2 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-orange-500 outline-none"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">- Marks</label>
                                        <input
                                            type="number"
                                            step="0.1"
                                            value={editingPaper.negativeMarks}
                                            onChange={e => setEditingPaper({ ...editingPaper, negativeMarks: parseFloat(e.target.value) })}
                                            className="w-full p-2 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-orange-500 outline-none"
                                        />
                                    </div>
                                </div>
                                <div className="flex items-center">
                                    <input
                                        type="checkbox"
                                        id="editIsPremium"
                                        checked={editingPaper.isPremium || false}
                                        onChange={e => setEditingPaper({ ...editingPaper, isPremium: e.target.checked })}
                                        className="w-4 h-4 text-orange-600 border-slate-300 dark:border-slate-700 rounded focus:ring-orange-500"
                                    />
                                    <label htmlFor="editIsPremium" className="ml-2 block text-sm font-medium text-slate-700 dark:text-slate-300">
                                        Premium Content
                                    </label>
                                </div>
                            </form>
                        </div>
                        <div className="p-4 border-t border-slate-100 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 flex justify-end gap-3">
                            <button
                                onClick={() => setEditingPaper(null)}
                                className="px-4 py-2 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 rounded font-medium"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                form="edit-paper-form"
                                className="px-4 py-2 bg-slate-900 dark:bg-slate-700 hover:bg-slate-800 dark:hover:bg-slate-600 text-white rounded font-medium transition-colors"
                            >
                                Save Changes
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminPapers;
